import * as faceapi from "@vladmandic/face-api";

const MODEL_URL = "/models"; // public/models/ berisi file .bin + .json
let modelsLoaded = false;
let loadPromise: Promise<void> | null = null;

/**
 * Lazy-load model face-api.js (MobileFaceNet).
 * Panggil sekali saja per sesi browser dengan singleton promise.
 */
export async function loadFaceModels(): Promise<void> {
  if (modelsLoaded) return;
  if (loadPromise) return loadPromise;

  loadPromise = Promise.all([
    faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
    faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
    faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
  ])
    .then(() => {
      modelsLoaded = true;
    })
    .catch((err) => {
      // Reset singleton agar pemanggil berikutnya bisa retry
      loadPromise = null;
      throw err;
    });

  return loadPromise;
}

/**
 * Ekstrak embedding wajah dari file image (Blob).
 * Returns array of 128-dim embeddings (satu per wajah terdeteksi).
 * Error jika tidak ada wajah terdeteksi.
 */
export async function extractEmbeddings(
  blob: Blob
): Promise<number[][]> {
  if (!modelsLoaded) {
    throw new Error("Face models belum di-load. Panggil loadFaceModels() terlebih dahulu.");
  }

  const img = await faceapi.bufferToImage(blob);

  const options = new faceapi.TinyFaceDetectorOptions({
    inputSize: 320, // Rendah untuk hemat CPU mobile
    scoreThreshold: 0.5,
  });

  const detections = await faceapi
    .detectAllFaces(img, options)
    .withFaceLandmarks(true) // tinyLandmarks
    .withFaceDescriptors();

  if (detections.length === 0) {
    throw new Error("Tidak ada wajah terdeteksi dalam foto.");
  }

  return detections.map((d) => Array.from(d.descriptor));
}

/**
 * Extract embedding dari element video HTML (live kamera).
 */
export async function extractEmbeddingFromVideo(
  videoEl: HTMLVideoElement
): Promise<number[]> {
  if (!modelsLoaded) {
    throw new Error("Face models belum di-load.");
  }

  const options = new faceapi.TinyFaceDetectorOptions({
    inputSize: 320,
    scoreThreshold: 0.5,
  });

  const detections = await faceapi
    .detectSingleFace(videoEl, options)
    .withFaceLandmarks(true)
    .withFaceDescriptor();

  if (!detections) {
    throw new Error("Tidak ada wajah terdeteksi. Hadap kamera dengan jelas.");
  }

  return Array.from(detections.descriptor);
}

/**
 * Hitung cosine similarity antara dua embedding array.
 */
export function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length || a.length === 0) return 0;
  let dot = 0, normA = 0, normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  return denom === 0 ? 0 : dot / denom;
}
